/**
 * AppSidebar — dark navigation rail used by both stake and ward desktop views.
 *
 * The visual treatment (dark theme, brand block, grouped sections, collapse
 * toggle, badges) was extracted from the original StakeSidebar so the ward
 * can share the same look while keeping its own links and brand text.
 *
 * Consumers supply:
 *   - brand: pre-computed two-line brand block + monogram for collapsed mode
 *   - dashboard: optional top item rendered without a section header
 *   - sections: grouped nav items in display order
 *   - settings: optional bottom item pinned above the collapse toggle
 *
 * Two widths:
 *   - Expanded (240px): brand block, section headers, icon + label.
 *   - Collapsed (64px):  monogram, icons only with native tooltips.
 */
import React from 'react'

export const SIDEBAR_WIDTH_EXPANDED = 240
export const SIDEBAR_WIDTH_COLLAPSED = 64

const NAV_ICON_SIZE = 16

export type NavItem = { id: string; label: string; icon: string; badge?: number }
export type NavSection = { title: string; items: NavItem[] }
export type Brand = { line1: string; line2: string; monogram: string }

// Format a stake org name into the design's two-line brand block.
// "Driggs Idaho Stake" → line1 "Driggs · Idaho" (italic), line2 "Stake leadership".
export function formatStakeBrand(orgName: string | undefined): Brand {
  const name = (orgName || '').trim()
  if (!name) return { line1: 'Stake', line2: 'Leadership', monogram: 'S' }
  const isStake = /\s+stake\s*$/i.test(name)
  const trimmed = isStake ? name.replace(/\s+stake\s*$/i, '').trim() : name
  const line1 = trimmed.split(/\s+/).join(' · ')
  const monogram = (trimmed[0] || name[0] || 'S').toUpperCase()
  return { line1, line2: isStake ? 'Stake leadership' : 'Leadership', monogram }
}

// Format a ward org name into the matching two-line brand block.
// "Driggs 1st Ward" → line1 "Driggs · 1st" (italic), line2 "Ward bishopric".
// "Driggs Ward 1"   → line1 "Driggs · 1" (italic), line2 "Ward bishopric".
export function formatWardBrand(orgName: string | undefined): Brand {
  const name = (orgName || '').trim()
  if (!name) return { line1: 'Ward', line2: 'Bishopric', monogram: 'W' }
  const isWard = /\s+ward(\s+\S+)?\s*$/i.test(name) || /\s+ward\s*$/i.test(name)
  // Strip trailing "Ward" or "Ward N" so the remaining tokens form line1.
  const trimmed = name
    .replace(/\s+ward(\s+\S+)?\s*$/i, (m) => {
      const tail = m.trim().split(/\s+/).slice(1).join(' ')
      return tail ? ' ' + tail : ''
    })
    .trim()
  const base = trimmed || name
  const line1 = base.split(/\s+/).join(' · ')
  const monogram = (base[0] || name[0] || 'W').toUpperCase()
  return { line1, line2: isWard ? 'Ward bishopric' : 'Bishopric', monogram }
}

interface AppSidebarProps {
  brand: Brand
  dashboard?: NavItem
  sections: NavSection[]
  settings?: NavItem
  activeTab: string
  navigateTo: (id: string) => void
  collapsed: boolean
  onToggleCollapsed: () => void
  // Optional per-tab badge counts (renders a small pill on the right when > 0).
  badges?: Record<string, number>
  // When false, the Settings nav button is hidden — used to gate the
  // panel by role. Defaults to true so existing callsites are unaffected.
  showSettings?: boolean
}

export default function AppSidebar({
  brand,
  dashboard,
  sections,
  settings,
  activeTab,
  navigateTo,
  collapsed,
  onToggleCollapsed,
  badges,
  showSettings = true,
}: AppSidebarProps) {
  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED

  return (
    <aside
      className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 z-30"
      style={{
        width,
        background: 'var(--nav-bg)',
        color: '#e6e6e6',
        transition: 'width 160ms var(--ease-out, ease)',
      }}
    >
      {/* Brand block */}
      <div
        className="flex-shrink-0"
        style={{
          padding: collapsed ? '20px 0 16px' : '20px 16px 16px',
          borderBottom: '1px solid #1f1f1f',
          textAlign: collapsed ? 'center' : 'left',
        }}
      >
        {collapsed ? (
          <div
            className="font-accent italic"
            style={{
              fontSize: 18,
              color: 'var(--accent-300, #8ea5ce)',
              letterSpacing: '0.02em',
              lineHeight: 1,
            }}
            title={`${brand.line1} — ${brand.line2}`}
          >
            {brand.monogram}
          </div>
        ) : (
          <>
            <div
              className="font-accent italic"
              style={{
                fontSize: 11,
                color: 'var(--accent-300, #8ea5ce)',
                letterSpacing: '0.02em',
              }}
            >
              {brand.line1}
            </div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 500,
                color: '#ffffff',
                marginTop: 2,
                letterSpacing: '-0.005em',
              }}
            >
              {brand.line2}
            </div>
          </>
        )}
      </div>

      {/* Scrollable nav — hide the dark-mode scrollbar (defaults render as a
          thick light bar against the near-black rail). */}
      <nav className="flex-1 overflow-y-auto hide-scrollbar" style={{ padding: '12px 8px' }}>
        {dashboard && (
          <NavButton
            item={dashboard}
            active={activeTab === dashboard.id}
            onClick={navigateTo}
            collapsed={collapsed}
            badge={badges?.[dashboard.id]}
          />
        )}

        {sections.map((sec) => (
          <div key={sec.title} style={{ marginTop: collapsed ? 12 : 18 }}>
            {collapsed ? (
              <div
                aria-hidden
                style={{
                  height: 1,
                  background: '#1f1f1f',
                  margin: '6px 12px',
                }}
              />
            ) : (
              <SectionHeader>{sec.title}</SectionHeader>
            )}
            <div style={{ marginTop: 4 }}>
              {sec.items.map((item) => (
                <NavButton
                  key={item.id}
                  item={item}
                  active={activeTab === item.id}
                  onClick={navigateTo}
                  collapsed={collapsed}
                  badge={badges?.[item.id]}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Collapse toggle + Settings pinned to bottom */}
      <div className="flex-shrink-0" style={{ padding: '8px 8px 16px' }}>
        <CollapseToggle collapsed={collapsed} onClick={onToggleCollapsed} />
        {settings && showSettings && (
          <NavButton
            item={settings}
            active={activeTab === settings.id}
            onClick={navigateTo}
            collapsed={collapsed}
          />
        )}
      </div>
    </aside>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#757575',
        padding: '4px 10px',
      }}
    >
      {children}
    </div>
  )
}

function NavButton({
  item,
  active,
  onClick,
  collapsed,
  badge,
}: {
  item: NavItem
  active: boolean
  onClick: (id: string) => void
  collapsed: boolean
  badge?: number
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(item.id)}
      title={collapsed ? item.label : undefined}
      aria-label={item.label}
      className="w-full transition-colors"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 10,
        padding: collapsed ? '10px 0' : '8px 10px',
        borderRadius: 8,
        background: active ? 'var(--nav-selected, #1a1a1a)' : 'transparent',
        color: active ? '#ffffff' : '#e6e6e6',
        fontSize: 13,
        fontWeight: 500,
        textAlign: 'left',
        transition: 'background 120ms var(--ease-out, ease), color 120ms var(--ease-out, ease)',
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'var(--nav-hover, #232323)'
          e.currentTarget.style.color = '#ffffff'
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'transparent'
          e.currentTarget.style.color = '#e6e6e6'
        }
      }}
    >
      <ion-icon
        name={item.icon}
        style={{
          fontSize: NAV_ICON_SIZE,
          color: active ? 'var(--accent-300, #8ea5ce)' : '#757575',
          flexShrink: 0,
        }}
      />
      {!collapsed && <span style={{ flex: 1 }}>{item.label}</span>}
      {!collapsed && badge !== undefined && badge > 0 && (
        <span
          style={{
            minWidth: 20,
            height: 18,
            padding: '0 6px',
            borderRadius: 999,
            background: active ? 'var(--accent-700, #1d2e4a)' : '#2a2a2a',
            color: active ? '#ffffff' : '#c6c6c6',
            fontSize: 11,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
          }}
        >
          {badge}
        </span>
      )}
    </button>
  )
}

function CollapseToggle({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-pressed={collapsed}
      className="w-full transition-colors"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: collapsed ? '10px 0' : '8px 10px',
        marginBottom: 4,
        borderRadius: 8,
        background: 'transparent',
        color: '#757575',
        cursor: 'pointer',
        transition: 'background 120ms var(--ease-out, ease), color 120ms var(--ease-out, ease)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--nav-hover, #232323)'
        e.currentTarget.style.color = '#ffffff'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent'
        e.currentTarget.style.color = '#757575'
      }}
    >
      <ion-icon
        name={collapsed ? 'chevron-forward-outline' : 'chevron-back-outline'}
        style={{ fontSize: NAV_ICON_SIZE, color: 'currentColor', flexShrink: 0 }}
      />
    </button>
  )
}
